import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import {
  deleteContentByID,
  manage,
  retagVideo,
} from "../../services/contentServices";
import TopAppBar from "../../components/TopAppBar";
import { useContentStore } from "../../stores/contentStore";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { VideoProps } from "../../types/VideoProps";
import { handleError, mapStatus } from "../../utils/utilFunctions";
import Footer from "../../components/Footer";
import PageRecords from "../../components/PageRecords";
import { locale } from "../../utils/LocaleManager";
import Pagination from "../../components/Pagination";
import { Status, TaggingStatus } from "../../common/statusEnum";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { StatusWrapper, TaggingFilter, TaggingFilterWrapper } from "./styled";
import moment from "moment";
import DataTable from "../../components/DataTable";
import ThumbnailMenu from "../../components/ThumbnailMenu";
import DialogBox from "../../components/DialogBox";
import { Bounce, toast } from "react-toastify";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Sort } from "../../enums/sort";
import { SortKeys } from "../../enums/sortKeys";
import { antiFlashWhite, black } from "../../utils/StyleHelpers";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";

const NUM_ITEMS = 10;

const ManageScreen = () => {
  const rolePermissions = useRolePermissionsStore(
    (state) => state.rolePermissions,
  );
  const isLoading = useContentStore((state: any) => state.isLoading);
  const setIsLoading = useContentStore((state: any) => state.setIsLoading);

  const [videoList, setVideoList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [selectedValue, setSelectedValue] = useState(0);
  const [pageSize, setPageSize] = useState(NUM_ITEMS);
  const [status, setStatus] = useState("");
  const [isShowTagging, setIsShowTagging] = useState(true);
  const [isShowCompleted, setIsShowCompleted] = useState(true);
  const [isShowError, setIsShowError] = useState(true);
  const [selected, setSelected] = useState(false);
  const [selectedVideoIndexId, setSelectedVideoIndexId] = useState("");
  const [sortKey, setSortKey] = useState(SortKeys.DateModified);
  const [sortOrder, setSortOrder] = useState(Sort.Descending);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const checkboxStyle = { paddingLeft: 0, paddingTop: 0, paddingBottom: 0 };
  const selectedThumbnails = useContentStore(
    (state: any) => state.selectedThumbnails,
  );
  const setSelectedThumbnails = useContentStore(
    (state: any) => state.setSelectedThumbnails,
  );
  const isShowDeleteDialog = useContentStore(
    (state: any) => state.isShowDeleteDialog,
  );
  const setIsShowDeleteDialog = useContentStore(
    (state: any) => state.setIsShowDeleteDialog,
  );

  const onChangeFilter = (event: { target: { value: any } }) => {
    setIsLoading(true);
    setSelectedValue(event.target.value);
    setPageSize(event.target.value);
  };

  const onChangePageRow = (_event: any, value: number) => {
    setIsLoading(true);
    setPageIndex(value);
    fetchData({
      pageSize,
      pageIndex: value,
      status,
      sortKey,
      sortOrder,
    });
  };

  const onChangePageIndex = (value: number) => {
    if (totalPages >= value) {
      setIsLoading(true);
      setPageIndex(value);
      fetchData({
        pageSize,
        pageIndex: value,
        status,
        sortKey,
        sortOrder,
      });
    }
  };

  const isShowFooter = () => totalRecords > NUM_ITEMS;

  const fetchData = async (params: any) => {
    const searchData = await manage(params);
    setIsLoading(false);
    if (searchData.status === 200) {
      const data = searchData.data;
      setVideoList(data.items);
      setTotalRecords(data.totalCount);
      setTotalPages(data.totalPages);
      setPageIndex(data.pageIndex);
      setPageSize(data.pageSize);
    } else {
      handleError({
        status: searchData.response.status,
        toast,
        errorMessage: `Error: Encountered an error fetching list of video. (CODE: ${searchData.code})`,
        onClose: () => {},
      });
    }
  };

  const onRefresh = () => {
    setIsLoading(true);
    fetchData({
      pageSize,
      pageIndex,
      status,
      sortKey,
      sortOrder,
    });
  };

  const renderDeleteDialog = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack spacing={1}>
          {selectedThumbnails.map((thumbnail: any) => (
            <Card key={thumbnail.videoIndexId} variant="outlined" style={{ color: "#666", fontSize: "20px", fontWeight: 400, lineHeight: "28px" }}>
              <CardContent style={{ padding: "1.25rem" }}>
                <Stack direction="row" gap="0.625rem">
                  {thumbnail.name}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderStatus = (status: string) => {
    const mappedStatus = mapStatus(status);
    switch (mappedStatus) {
      case Status.completed:
        return (
          <StatusWrapper>
            <CheckCircleOutlineIcon color={"success"} /> {Status.completed}
          </StatusWrapper>
        );
      case Status.tagging:
        return (
          <StatusWrapper>
            <CircularProgress color="inherit" size={24} /> {Status.tagging}...
          </StatusWrapper>
        );
      default:
        return (
          <StatusWrapper>
            <HighlightOffIcon color={"error"} /> {Status.error}
          </StatusWrapper>
        );
    }
  };

  const renderDate = (date: string) => {
    return moment(date).format("MMMM D, YYYY");
  };

  const renderNumber = (index: number) => {
    const numPerPage = selectedValue > 0 ? selectedValue : NUM_ITEMS;
    if (pageIndex > 1) return index + 1 + (pageIndex - 1) * numPerPage;

    return index + 1;
  };

  const getCheckBoxStyle = (isChecked: boolean) => {
    if (isChecked)
      return {
        background: "rgba(193, 230, 254, 1)",
        padding: "0.625rem 0.75rem",
        borderRadius: "0.5rem",
      };

    return {
      background: "#FFF",
      padding: "0.625rem 0.75rem",
      border: "1px solid #000",
      borderRadius: "0.5rem",
    };
  };

  const onContextMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    selectedVideo: VideoProps,
  ) => {
    e.preventDefault();
    setSelectedVideoIndexId(selectedVideo.videoIndexId);
    setSelected(true);
    setAnchorEl(null);
    setContextMenu({
      mouseX: e.clientX + 2,
      mouseY: e.clientY - 6,
    });
    setOpen(true);
    setSelectedThumbnails([]);
    setSelectedThumbnails([
      {
        videoIndexId: selectedVideo?.videoIndexId,
        name: selectedVideo?.filename,
      },
    ]);
  };

  const deleteVideos = async () => {
    if (!rolePermissions?.video?.delete) return;

    setIsShowDeleteDialog(false);
    toast.warning(`Processing: Deleting ${selectedThumbnails.length} video/s`, {
      position: "bottom-left",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: true,
      pauseOnFocusLoss: false,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });

    const [forDeletion] = selectedThumbnails;
    const deleted = await deleteContentByID(forDeletion.videoIndexId);
    setSelectedThumbnails([]);
    setSelected(false);
    setSelectedVideoIndexId("");

    if (deleted.status === 200) {
      toast.success("Video Deleted", {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
      setIsLoading(true);
      fetchData({
        pageSize,
        pageIndex,
        status,
        sortKey,
        sortOrder,
      });
    } else {
      handleError({
        status: deleted.response.status,
        toast,
        errorMessage: `Error: Encountered an error while deleting the video (CODE: ${deleted.code})`,
        onClose: () => {},
      });
    }
  };

  const onRetag = async () => {
    if (!rolePermissions?.video?.retag) return;

    const retagging = await retagVideo(selectedVideoIndexId);
    setSelected(false);
    setSelectedThumbnails([]);
    setSelectedVideoIndexId("");
    if (retagging.status === 200) {
      toast.success("Re-tag video Success", {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
      setIsLoading(true);
      fetchData({
        pageSize,
        pageIndex,
        status,
        sortKey,
        sortOrder,
      });
    } else {
      handleError({
        status: retagging.response.status,
        toast,
        errorMessage: `Error: Encountered an error while retagging the video (CODE: ${retagging.code})`,
        onClose: () => {},
      });
    }
  };

  const renderLink = (text: string, key: any) => {
    return (
      <Link
        component="button"
        onClick={() => {
          setSortKey(key);
          setSortOrder((prevSortOrder) => {
            if (key !== sortKey) return Sort.Ascending;

            return prevSortOrder === Sort.Ascending
              ? Sort.Descending
              : Sort.Ascending;
          });
        }}
        style={{
          display: "flex",
          color: "black",
          textDecoration: "none",
          fontSize: "1.25rem",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {text}
        {sortKey === key ? (
          sortOrder === "asc" ? (
            <ArrowUpwardIcon />
          ) : (
            <ArrowDownwardIcon />
          )
        ) : sortOrder === "asc" ? (
          <ArrowDownwardIcon style={{ color: "rgba(0, 0, 0, 0.23)" }} />
        ) : (
          <ArrowUpwardIcon style={{ color: "rgba(0, 0, 0, 0.23)" }} />
        )}
      </Link>
    );
  };

  useEffect(() => {
    if (!pageIndex) return;
    setIsLoading(true);
    fetchData({
      pageSize,
      pageIndex,
      status,
      sortKey,
      sortOrder,
    }); // eslint-disable-next-line
  }, [pageSize, status, sortKey, sortOrder]);

  useEffect(() => {
    const enabledStatus = [];

    if (isShowTagging && isShowCompleted && isShowError) {
      setStatus("");
      return;
    }

    if (isShowTagging) enabledStatus.push(TaggingStatus.processing);

    if (isShowCompleted) enabledStatus.push(TaggingStatus.processed);

    if (isShowError) enabledStatus.push(TaggingStatus.failed);

    setStatus(enabledStatus.join(","));
  }, [isShowTagging, isShowCompleted, isShowError]);

  return (
    <>
      <TopAppBar nav={"manage"} handleLogout={() => {}}></TopAppBar>
      <MainLayout nav={"manage"}>
        <Stack
          direction="row"
          style={{
            borderRadius: "0.5rem",
            padding: "0.25rem",
            gap: "2.5rem",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "2.5rem",
          }}
          justifyContent={"center"}
        >
          <Stack
            direction="row"
            style={{
              padding: "0.25rem",
              gap: "2.5rem",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <TaggingFilter>Tagging Status:</TaggingFilter>
            <TaggingFilterWrapper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isShowTagging}
                    onChange={(_event: any, checked: boolean) =>
                      setIsShowTagging(checked)
                    }
                    style={checkboxStyle}
                  />
                }
                label={Status.tagging}
                style={getCheckBoxStyle(isShowTagging)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isShowCompleted}
                    onChange={(_event: any, checked: boolean) =>
                      setIsShowCompleted(checked)
                    }
                    style={checkboxStyle}
                  />
                }
                label={Status.completed}
                style={getCheckBoxStyle(isShowCompleted)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isShowError}
                    onChange={(_event: any, checked: boolean) =>
                      setIsShowError(checked)
                    }
                    style={checkboxStyle}
                  />
                }
                label={Status.error}
                style={getCheckBoxStyle(isShowError)}
              />
            </TaggingFilterWrapper>
          </Stack>
          <Stack
            direction="row"
            style={{
              padding: "0.25rem",
              gap: "2.5rem",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              style={{
                borderRadius: "0.5rem",
                padding: "0.625rem 0.75rem",
                alignSelf: "flex-end",
              }}
              onClick={() => onRefresh()}
            >
              {locale.refresh}
            </Button>
          </Stack>
        </Stack>
        <DataTable
          minWidth={650}
          label="manage video table"
          tableHeader={
            <>
              <TableCell
                style={{
                  display: "flex",
                  color: black,
                  textDecoration: "none",
                  fontSize: "1.25rem",
                  alignItems: "center",
                  gap: "1rem",
                  backgroundColor: antiFlashWhite,
                }}
              >
                #
              </TableCell>
              <TableCell
                style={{
                  width: "60%",
                  backgroundColor: antiFlashWhite,
                }}
              >
                {renderLink("Video Title", SortKeys.Title)}
              </TableCell>
              <TableCell
                style={{
                  width: "15%",
                  backgroundColor: antiFlashWhite,
                }}
              >
                {renderLink("Date Modified", SortKeys.DateModified)}
              </TableCell>
              <TableCell
                style={{
                  width: "15%",
                  backgroundColor: antiFlashWhite,
                }}
              >
                {renderLink("Tagging Status", SortKeys.Status)}
              </TableCell>
              <TableCell
                align="center"
                style={{
                  width: "5%",
                  backgroundColor: antiFlashWhite,
                }}
              ></TableCell>
            </>
          }
          tableBody={
            <TableBody>
              {!isLoading && videoList.length ? (
                videoList.map((video: VideoProps, index: number) => (
                  <TableRow
                    key={video.videoIndexId}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    onContextMenu={(e) => {
                      onContextMenuOpen(e, video);
                    }}
                    style={{
                      background:
                        selected && selectedVideoIndexId === video.videoIndexId
                          ? "#C1E7FE"
                          : "",
                    }}
                    hover={true}
                  >
                    <TableCell component="th" scope="row">
                      {renderNumber(index)}
                    </TableCell>
                    <TableCell>{video.name}</TableCell>
                    <TableCell>{renderDate(video.dateModified)}</TableCell>
                    <TableCell>{renderStatus(video.state)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        aria-label={`Open actions for video ${video.name}`}
                        onClick={(e) => onContextMenuOpen(e, video)}
                        style={{ color: "#000" }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <></>
              )}
            </TableBody>
          }
        />
        <ThumbnailMenu
          open={open}
          setOpen={setOpen}
          anchorEl={anchorEl}
          setSelected={setSelected}
          videoIndexId={0}
          contextMenu={contextMenu}
          isManage={true}
          onRetag={() => onRetag()}
        />
      </MainLayout>
      {isShowFooter() ? (
        <Footer marginLeft={"-30px"} width="calc(100% - 79px)">
          <PageRecords
            records={totalRecords}
            pages={totalPages}
            selectedValue={selectedValue}
            onChangePageSize={onChangeFilter}
            defaultValue={NUM_ITEMS}
          />
          <Pagination
            name={locale.goToPage}
            pages={totalPages}
            pageIndex={pageIndex}
            onChangePageIndex={onChangePageIndex}
            onChangePageRow={onChangePageRow}
            activePage={pageIndex}
            marginRight=""
          />
        </Footer>
      ) : null}
      {isShowDeleteDialog ? (
        <DialogBox
          header={locale.deleteHeader}
          body={<div>{locale.deleteBody}</div>}
          subBody={renderDeleteDialog()}
          onCancel={() => {
            setIsShowDeleteDialog(false);
            setSelected(false);
            setSelectedVideoIndexId("");
          }}
          onSubmit={() => deleteVideos()}
          isDelete={true}
          primaryButton={locale.yes}
          secondaryButton={locale.no}
        ></DialogBox>
      ) : (
        <></>
      )}
    </>
  );
};

export default ManageScreen;
